import { useCallback } from "react";
import useAjax from "@apis/useAjax";
import {
	GET_CLUB_INVENTORY_OPTIONS_API,
	GET_CLUB_INVENTORY_SEARCH_STOCK_SINGLE_API,
	POST_CLUB_INVENTORY_COUNT_API,
	POST_CLUB_INVENTORY_OVERVIEW_API,
	GET_CLUB_INVENTORY_DETAIL_API,
	PUT_CLUB_INVENTORY_QUANTITY_ADJUSTMENT_API,
	POST_CLUB_INVENTORY_DEFECTIVE_STOCK_IN_API,
	POST_CLUB_INVENTORY_DEFECTIVE_REPORT_API,
	POST_CLUB_INVENTORY_RECORDS_API,
	GET_CLUB_INVENTORY_DEFECTIVE_RECORDS_API,
	POST_CLUB_INVENTORY_DEFECTIVE_RECORDS_API,
} from "@apis/apiPath";

/**
 * 店鋪庫存管理相關 API Hook
 * 對應後端 ClubInventoryController
 */
const useClubInventoryApi = () => {
	const Ajax = useAjax();

	/**
	 * 取得庫存頁面選項（分類、教練、異動紀錄區間）
	 */
	const getClubInventoryOptionsApi = useCallback(
		async () => await Ajax.get(GET_CLUB_INVENTORY_OPTIONS_API),
		// eslint-disable-next-line
		[],
	);

	/**
	 * 建立盤點單
	 * @param {object} params ClubInventoryCountRequestModel
	 */
	const postClubInventoryCountApi = useCallback(
		async params => await Ajax.post(POST_CLUB_INVENTORY_COUNT_API, params),
		// eslint-disable-next-line
		[],
	);

	/**
	 * 取得庫存總覽(分頁列表)
	 * @param {object} params ClubInventoryPaginationRequestModel
	 */
	const postClubInventoryOverviewApi = useCallback(
		async params => {
			return await Ajax.post(POST_CLUB_INVENTORY_OVERVIEW_API, params);
		},
		// eslint-disable-next-line
		[],
	);

	/**
	 * 取得商品庫存明細
	 * @param {object} params
	 * @param {number} params.productID 商品ID
	 * @param {number} params.variantID 規格ID
	 */
	const getClubInventoryDetailApi = useCallback(
		async ({ productID, variantID }) => await Ajax.get(GET_CLUB_INVENTORY_DETAIL_API(productID, variantID)),
		// eslint-disable-next-line
		[],
	);

	/**
	 * SKU 精準搜尋商品庫存
	 * @param {object} params
	 * @param {string} params.text SKU
	 */
	const getClubInventorySearchStockSingleApi = useCallback(
		async ({ text }) => await Ajax.get(GET_CLUB_INVENTORY_SEARCH_STOCK_SINGLE_API, { text }),
		// eslint-disable-next-line
		[],
	);

	/**
	 * 調整庫存量（盤盈/盤虧）
	 * @param {object} params ClubInventoryAdjustmentRequestModel
	 */
	const putClubInventoryQuantityAdjustmentApi = useCallback(
		async params => await Ajax.put(PUT_CLUB_INVENTORY_QUANTITY_ADJUSTMENT_API, params),
		// eslint-disable-next-line
		[],
	);

	/**
	 * 新增瑕疵入庫
	 * @param {object} params ClubInventoryAdjustmentRequestModel
	 */
	const postClubInventoryDefectiveStockInApi = useCallback(
		async params => await Ajax.post(POST_CLUB_INVENTORY_DEFECTIVE_STOCK_IN_API, params),
		// eslint-disable-next-line
		[],
	);

	/**
	 * 新增瑕疵回報單（目前回報數量固定為 1）
	 * @param {object} params ClubInventoryDefectiveReportCreateRequestModel
	 */
	const postClubInventoryDefectiveReportApi = useCallback(
		async params => await Ajax.post(POST_CLUB_INVENTORY_DEFECTIVE_REPORT_API, params),
		// eslint-disable-next-line
		[],
	);

	/**
	 * 取得商品庫存異動紀錄（含明細與統計）
	 * @param {object} params
	 * @param {number} params.productID 商品ID
	 * @param {number} params.variantID 規格ID
	 * @param {number} [params.pageIndex]
	 * @param {number} [params.pageSize]
	 * @param {number} [params.recordRange]
	 * @param {string} [params.startDate]
	 * @param {string} [params.endDate]
	 * @param {number} [params.referenceType]
	 * @param {number} [params.recordType]
	 */
	const postClubInventoryRecordsApi = useCallback(
		async params => {
			const { productID, variantID, ...body } = params;
			return await Ajax.post(POST_CLUB_INVENTORY_RECORDS_API(productID, variantID), body);
		},
		// eslint-disable-next-line
		[],
	);

	/**
	 * 取得商品瑕疵紀錄列表（無分頁）
	 * @param {object} params
	 * @param {number} params.productID 商品ID
	 * @param {number} params.variantID 規格ID
	 */
	const getClubInventoryDefectiveRecordsApi = useCallback(
		async ({ productID, variantID }) => await Ajax.get(GET_CLUB_INVENTORY_DEFECTIVE_RECORDS_API(productID, variantID)),
		// eslint-disable-next-line
		[],
	);

	/**
	 * 取得商品瑕疵紀錄分頁列表（含商品明細與紀錄分頁）
	 * @param {object} params ClubDefectiveReportPageRequestModel
	 * @param {number} params.productID 商品ID
	 * @param {number} params.variantID 規格ID
	 * @param {number} [params.pageIndex]
	 * @param {number} [params.pageSize]
	 */
	const postClubInventoryDefectiveRecordsApi = useCallback(
		async params => await Ajax.post(POST_CLUB_INVENTORY_DEFECTIVE_RECORDS_API, params),
		// eslint-disable-next-line
		[],
	);

	return {
		getClubInventoryOptionsApi,
		getClubInventorySearchStockSingleApi,
		postClubInventoryCountApi,
		postClubInventoryOverviewApi,
		getClubInventoryDetailApi,
		putClubInventoryQuantityAdjustmentApi,
		postClubInventoryDefectiveStockInApi,
		postClubInventoryDefectiveReportApi,
		postClubInventoryRecordsApi,
		getClubInventoryDefectiveRecordsApi,
		postClubInventoryDefectiveRecordsApi,
	};
};

export default useClubInventoryApi;
